import { Link } from 'react-router-dom';
import { getPublished } from '@/data/loader';
import { useI18n, useLocalizedPath } from '@/hooks/useI18n';
import { EntityCard } from '@/components/EntityCard';
import { TrilhasSEO } from '@/components/SEO';
import { Container } from '@/components/Container';
import { OptimizedImage } from '@/components/OptimizedImage';
import { TrilhasQuiz } from '@/components/TrilhasQuiz';
import { AuthorBox } from '@/components/AuthorBox';
import { defaultAuthor } from '@/data/author';
import pilar from '@/data/trilhas-pilar.json';

const MARE_NOTAS: Record<string, string> = Object.fromEntries(
  (pilar.mare || []).map((m: any) => [m.handle, m.nota])
);

// "Ideal para" derivado dos dados de cada trilha
const IDEAL: Record<string, string> = {
  'trilha-das-encantadas': 'Iniciantes, crianças, a lenda',
  'trilha-do-farol': 'A vista 360° — o cartão-postal',
  'trilha-praia-do-miguel': 'Praia deserta + mirante',
  'trilha-da-fortaleza': 'História e canhões, ritmo leve',
  'trilha-encantadas-nova-brasilia': 'Travessia entre as vilas',
  'volta-completa-ilha': 'Trilheiros experientes',
};

export function TrilhasPage() {
  const { lang } = useI18n();
  const localize = useLocalizedPath();
  const trilhas = getPublished('trilha', lang);
  const byHandle: Record<string, any> = Object.fromEntries(trilhas.map((t: any) => [t.handle, t]));
  const faq = pilar.faq as { q: string; a: string }[];

  return (
    <>
      <TrilhasSEO trilhas={trilhas} />
      <div>
        {/* Hero */}
        <div className="relative mb-10">
          <div className="relative h-56 sm:h-72">
            <OptimizedImage
              src="/images/trilha-floresta.jpg"
              alt="Trilha na Mata Atlântica da Ilha do Mel"
              className="w-full h-full object-cover"
              priority
            />
            <div className="absolute inset-0 hero-gradient" />
          </div>
          <div className="absolute inset-0 flex items-center justify-center text-center px-4">
            <div>
              <h1 className="font-display text-5xl sm:text-6xl text-white mb-2">
                Trilhas da Ilha do Mel
              </h1>
              <p className="font-editorial text-xl text-white/80">
                As 6 rotas que ligam vilas, praias e mirantes — com distância, tempo e nível de cada uma
              </p>
            </div>
          </div>
        </div>

        <Container className="pb-20">
          {/* Intro */}
          <div className="max-w-3xl mx-auto mb-12 space-y-4">
            <p className="font-editorial text-lg text-warm-gray leading-relaxed">
              Na <Link to="/" className="text-teal-deep font-medium hover:underline">Ilha do Mel</Link> não
              existe carro — as trilhas são as ruas. É por elas que você vai do trapiche à pousada, da vila ao
              Farol, da Gruta à Fortaleza, sempre cortando Mata Atlântica com o mar aparecendo entre as árvores.
            </p>
            <p className="font-body text-warm-gray leading-relaxed">
              A boa notícia: tem caminhada pra todo perfil, da passarela de 15 minutos até um circuito de 20 km.
              Abaixo você compara as 6 rotas lado a lado, faz o teste pra descobrir a sua e vê o guia completo de
              cada uma — com maré, calçado e o que levar.
            </p>
          </div>

          {/* Tabela comparativa */}
          <section className="mb-16" id="comparativo">
            <h2 className="font-display text-3xl sm:text-4xl text-warm-dark mb-2">As 6 rotas lado a lado</h2>
            <p className="font-editorial text-warm-gray mb-6">
              Distância, tempo, nível e ponto de partida — pra escolher de um golpe de vista.
            </p>
            <div className="overflow-x-auto rounded-2xl border border-sand-200">
              <table className="w-full text-left border-collapse min-w-[720px]">
                <thead>
                  <tr className="bg-sand-50 text-warm-dark">
                    <th className="font-display text-sm p-3">Trilha</th>
                    <th className="font-display text-sm p-3">Partida</th>
                    <th className="font-display text-sm p-3">Distância</th>
                    <th className="font-display text-sm p-3">Tempo</th>
                    <th className="font-display text-sm p-3">Nível</th>
                    <th className="font-display text-sm p-3">Ideal para</th>
                  </tr>
                </thead>
                <tbody>
                  {trilhas.map((t: any, i: number) => (
                    <tr key={t.handle} className={`border-t border-sand-100 ${i % 2 ? 'bg-sand-50/40' : 'bg-white'}`}>
                      <td className="p-3 font-body text-sm font-medium text-warm-dark">
                        <Link to={localize(`/trilha/${t.handle}`)} className="hover:text-teal-deep hover:underline">
                          {t.title}
                        </Link>
                        {MARE_NOTAS[t.handle] && (
                          <span className="block font-body text-[11px] text-coral mt-0.5">🌊 {MARE_NOTAS[t.handle]}</span>
                        )}
                      </td>
                      <td className="p-3 font-body text-sm text-warm-gray">{t.startPoint}</td>
                      <td className="p-3 font-body text-sm text-warm-gray">{t.distance}</td>
                      <td className="p-3 font-body text-sm text-warm-gray">{t.duration}</td>
                      <td className="p-3 font-body text-sm text-warm-gray">{t.difficulty}</td>
                      <td className="p-3 font-body text-sm text-warm-gray">{IDEAL[t.handle] || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* Quiz */}
          <TrilhasQuiz trilhas={trilhas} />

          {/* Grupos por vila de partida */}
          {(pilar.grupos as any[]).map(g => {
            const items = g.handles.map((h: string) => byHandle[h]).filter(Boolean);
            if (!items.length) return null;
            return (
              <section key={g.vila} className="mb-16" id={`partida-${g.vila.toLowerCase().replace(/\s/g, '-')}`}>
                <h2 className="flex items-center gap-2 font-display text-3xl sm:text-4xl text-warm-dark mb-2">
                  <span aria-hidden="true">{g.icon}</span>
                  {g.vila === 'Circuito' ? 'O circuito completo' : `Saindo de ${g.vila}`}
                </h2>
                <p className="font-editorial text-warm-gray mb-6 max-w-3xl">{g.lead}</p>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {items.map((t: any) => (
                    <EntityCard key={t.handle} entity={t} category="trilha" />
                  ))}
                </div>
              </section>
            );
          })}

          {/* Como escolher por dificuldade */}
          <section className="mb-16" id="por-dificuldade">
            <h2 className="font-display text-3xl sm:text-4xl text-warm-dark mb-6">Escolhendo pelo fôlego</h2>
            <div className="grid sm:grid-cols-3 gap-4">
              {[
                { t: '🍃 Leves', d: 'Gruta das Encantadas (30–45 min) e Fortaleza (plana, ~2h ida e volta). Dá até de chinelo firme — mas tênis é melhor.', hs: ['trilha-das-encantadas', 'trilha-da-fortaleza'] },
                { t: '🥾 Moderadas', d: 'Farol (escadaria de ~140 degraus com a melhor vista da ilha), Praia do Miguel (mirante + praia deserta) e a travessia entre as vilas.', hs: ['trilha-do-farol', 'trilha-praia-do-miguel', 'trilha-encantadas-nova-brasilia'] },
                { t: '🔥 Pra quem treina', d: 'A Volta Completa: ~20 km e ~8 horas costurando praias, morros e costões. Saia cedo, leve água e confira a maré.', hs: ['volta-completa-ilha'] },
              ].map(c => (
                <div key={c.t} className="p-5 rounded-2xl bg-white border border-sand-200">
                  <h3 className="font-display text-lg text-warm-dark mb-2">{c.t}</h3>
                  <p className="font-body text-sm text-warm-gray leading-relaxed mb-3">{c.d}</p>
                  <div className="flex flex-wrap gap-1.5">
                    {c.hs.map(h => byHandle[h] && (
                      <Link key={h} to={localize(`/trilha/${h}`)} className="font-body text-[11px] bg-teal-light/10 text-teal-deep px-2 py-1 rounded-full hover:bg-teal-light/20">
                        {byHandle[h].title} →
                      </Link>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Segurança, maré e o que levar */}
          <section className="mb-16" id="seguranca">
            <h2 className="font-display text-3xl sm:text-4xl text-warm-dark mb-6">Antes de calçar o tênis</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                { icon: '👟', t: 'Calçado fechado', d: 'Raízes, costões escorregadios e areia: tênis com boa aderência resolve. Chinelo só nas passarelas curtas.' },
                { icon: '🌊', t: 'Consulte a maré', d: 'A Gruta só se visita na maré baixa (~8h–13h) e trechos de praia da rota da Fortaleza bloqueiam na maré alta.' },
                { icon: '🔦', t: 'Lanterna e água', d: 'Não há iluminação nas trilhas nem bebedouros no caminho. Leve água, protetor e uma lanterna se for voltar no fim do dia.' },
                { icon: '🦟', t: 'Repelente e lixo', d: 'Mosquitos aparecem nos trechos de mata (Fortaleza, principalmente). E não há lixeiras: o que entra com você, sai com você.' },
              ].map(c => (
                <div key={c.t} className="flex items-start gap-3 p-5 rounded-2xl bg-white border border-sand-200">
                  <span className="text-2xl flex-shrink-0" aria-hidden="true">{c.icon}</span>
                  <div>
                    <h3 className="font-display text-lg text-warm-dark mb-1">{c.t}</h3>
                    <p className="font-body text-sm text-warm-gray leading-relaxed">{c.d}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* FAQ */}
          <section className="mb-16" id="faq">
            <h2 className="font-display text-3xl sm:text-4xl text-warm-dark mb-6">Perguntas frequentes</h2>
            <div className="space-y-3 max-w-3xl">
              {faq.map((f, i) => (
                <details key={i} className="group bg-white border border-sand-200 rounded-lg overflow-hidden">
                  <summary className="flex items-center justify-between p-4 cursor-pointer hover:bg-sand-50 list-none">
                    <span className="font-display text-base text-warm-dark pr-4">{f.q}</span>
                    <span className="text-teal-deep flex-shrink-0 transition-transform duration-200 group-open:rotate-180">▼</span>
                  </summary>
                  <div className="px-4 pb-4 font-body text-warm-gray text-sm leading-relaxed">{f.a}</div>
                </details>
              ))}
            </div>
          </section>

          {/* CTA cross-sell */}
          <section className="mb-16 bg-teal-deep rounded-2xl p-8 lg:p-12 text-center">
            <h2 className="font-display text-3xl sm:text-4xl text-white mb-3">Depois da trilha</h2>
            <p className="font-editorial text-white/80 mb-6 max-w-xl mx-auto">
              Escolha em qual praia mergulhar, onde comer um camarão e o que mais encaixar nos seus dias.
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              <Link to={localize('/o-que-fazer-ilha-do-mel')} className="inline-flex items-center gap-2 bg-white text-teal-deep hover:bg-sand-100 font-body font-semibold rounded-xl px-5 py-3 transition-all">O que fazer</Link>
              <Link to={localize('/praias-ilha-do-mel')} className="inline-flex items-center gap-2 bg-white text-teal-deep hover:bg-sand-100 font-body font-semibold rounded-xl px-5 py-3 transition-all">Praias</Link>
              <Link to={localize('/pousadas-ilha-do-mel')} className="inline-flex items-center gap-2 bg-white text-teal-deep hover:bg-sand-100 font-body font-semibold rounded-xl px-5 py-3 transition-all">Pousadas</Link>
            </div>
          </section>

          <AuthorBox author={defaultAuthor} />
        </Container>
      </div>
    </>
  );
}
